import { useRef, useMemo, useState } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';

import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';

import { dataGridPremiumColumns } from '../../utils/helper';
import {
  DataGridIdentifiers,
  FormErrorUI,
  AddButtonUi,
  AutoCompleteSelectUI,
  DatagridPremiumUi,
  InputUI,
  CheckboxUI,
} from '../Interface';
import useConfig from '../../context/ConfigContext';
import { Checklist } from '@mui/icons-material';
import ServiceTaskChecklistDialog from './ServiceTaskChecklistDialog';

const EditToolbar = (props) => {
  const { disabled, push, defaultServiceTask } = props;
  const { values, validateField } = useFormikContext();
  const { config } = useConfig();

  const handleClick = async () => {
    defaultServiceTask.id = uuidv4();
    defaultServiceTask.isNew = true;
    defaultServiceTask.qtyUnitId = config.defServiceOperationQtyUnit;
    await push(defaultServiceTask);

    await validateField(`serviceTask.${values.serviceTask.length ?? 0}.serviceOperationId`);
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
};

const ServiceTask = ({
  disabled,
  defaultServiceTask,
  defaultMaterial,
  autocompleteOptions,
  autocompleteData,
  selectedValuesState,
}) => {
  const { config } = useConfig();
  const { selectedValues } = selectedValuesState;
  const focusedFieldRef = useRef(null);
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const defaultDialogData = {
    open: false,
    serviceTaskId: null,
  };
  const [checklistDialogData, setChecklistDialogData] = useState(defaultDialogData);

  const getValueIndex = (values, id) => {
    return values.serviceTask.findIndex((rc) => rc.id === id);
  };

  const onOperationChange = (newVal, valueIndex) => {
    const line = values.serviceTask[valueIndex];
    const materials = values.serviceMaterial.filter((sm) => sm.serviceTaskId !== line.id);
    const checklists = values.serviceTaskCheckList.filter((sc) => sc.serviceTaskId !== line.id);

    if (!newVal) {
      setFieldValue(`serviceMaterial`, materials);
      setFieldValue(`serviceTaskCheckList`, checklists);
      setFieldValue(`serviceTask.${valueIndex}.serviceOperationId`, null);
      setFieldValue(`serviceTask.${valueIndex}.qtyUnitId`, null);
      setFieldValue(`serviceTask.${valueIndex}.plannedTime`, 1);
      setFieldValue(`serviceTask.${valueIndex}.attachRequired`, false);
      setFieldValue(`serviceTask.${valueIndex}.descriptionRequired`, false);

      return;
    }

    const operation = autocompleteData.serviceOperation.find((op) => op.id === newVal);

    if (!operation) return;

    setFieldValue(`serviceTask.${valueIndex}.serviceOperationId`, newVal);

    setFieldValue(`serviceTask.${valueIndex}.qtyUnitId`, operation.quantityUnitId);
    setFieldValue(`serviceTask.${valueIndex}.plannedTime`, operation.standardTime);
    setFieldValue(`serviceTask.${valueIndex}.attachRequired`, operation.attachRequired);
    setFieldValue(`serviceTask.${valueIndex}.descriptionRequired`, operation.descriptionRequired);

    const newMaterials = [];
    for (const material of operation.serviceMaterial) {
      const findedItem = autocompleteData?.items?.find((it) => it.itemCode === material.itemCode);

      newMaterials.push({
        ...defaultMaterial,
        id: uuidv4(),
        serviceTaskId: line.id,
        itemCode: material.itemCode,
        // @TODO from operationMaterial
        itemName: findedItem?.itemName || '',
        qty: material.qty,
        qtyUnitId: findedItem.quantityUnitId,
        isRequired: material.isRequired,
        note: material.note,
        isNew: true,
      });
    }
    setFieldValue(`serviceMaterial`, [...materials, ...newMaterials]);

    const newChecklists = [];
    for (const checklist of operation.serviceCheckList) {
      newChecklists.push({
        id: uuidv4(),
        serviceTaskId: line.id,
        name: checklist.name,
        note: checklist.note,
        attachRequired: checklist.attachRequired,
        descriptionRequired: checklist.descriptionRequired,
        isNew: true,
      });
    }
    setFieldValue(`serviceTaskCheckList`, [...checklists, ...newChecklists]);
  };

  const columns = useMemo(
    () => (remove) =>
      dataGridPremiumColumns([
        {
          field: 'label',
          headerName: '#',
          type: 'string',
          filterable: false,
          sortable: false,
        },
        {
          field: 'serviceOperationId',
          headerName: 'Szerviz tevékenység',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions.serviceOperation.find(
              (u) => u.value === values.serviceTask?.[valueIndex]?.serviceOperationId
            );
            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions.serviceOperation.find(
              (u) => u.value === values.serviceTask?.[valueIndex]?.serviceOperationId
            );
            return (
              <Field
                name={`serviceTask.${valueIndex}.serviceOperationId`}
                type="text"
                variant="standard"
                as={AutoCompleteSelectUI}
                fullWidth
                helperText={<FormErrorUI message={errors?.serviceTask?.[valueIndex]?.serviceOperationId} />}
                onChange={(_e, newVal) => {
                  const val = newVal?.value ?? null;
                  onOperationChange(val, valueIndex);

                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: val,
                  });
                }}
                onBlur={async () => await validateField(`serviceTask.${valueIndex}.serviceOperationId`)}
                value={findedItem}
                isOptionEqualToValue={(option, value) => option.value === value}
                dataset={autocompleteOptions.serviceOperation}
              />
            );
          },
        },

        {
          field: 'plannedTime',
          headerName: 'Tervezett ráfordítás',
          type: 'string',
          editable: true,
          sortable: false,
          valueGetter: (_data, row) => {
            const valueIndex = getValueIndex(values, row.id);
            return values?.serviceTask?.[valueIndex]?.plannedTime;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`serviceTask.${valueIndex}.plannedTime`}
                type="number"
                as={InputUI}
                variant="standard"
                fullWidth
                autoFocus={focusedFieldRef.current === 'plannedTime'}
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.target.select()}
                helperText={<FormErrorUI message={errors?.serviceTask?.[valueIndex]?.plannedTime} />}
                onChange={(e) => {
                  setFieldValue(`serviceTask.${valueIndex}.plannedTime`, e.target?.value);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target?.value,
                  });
                }}
                onBlur={async () => await validateField(`serviceTask.${valueIndex}.plannedTime`)}
              />
            );
          },
        },

        {
          field: 'qtyUnitId',
          headerName: 'Me.',
          type: 'string',
          filterable: false,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            const findedUnit = autocompleteData.quantityUnit.find(
              (u) => u.id === values.serviceTask?.[valueIndex]?.qtyUnitId
            );
            return findedUnit?.code ?? '';
          },
        },

        {
          field: 'workAreaId',
          headerName: 'Munkaállomás',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions.workArea.find(
              (u) => u.value === values.serviceTask?.[valueIndex]?.workAreaId
            );
            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions.workArea.find(
              (u) => u.value === values.serviceTask?.[valueIndex]?.workAreaId
            );
            return (
              <Field
                name={`serviceTask.${valueIndex}.workAreaId`}
                type="text"
                variant="standard"
                as={AutoCompleteSelectUI}
                fullWidth
                helperText={<FormErrorUI message={errors?.serviceTask?.[valueIndex]?.workAreaId} />}
                onChange={(_e, newVal) => {
                  const val = newVal?.value ?? null;

                  setFieldValue(`serviceTask.${valueIndex}.workAreaId`, val);

                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: val,
                  });
                }}
                onBlur={async () => await validateField(`serviceTask.${valueIndex}.workAreaId`)}
                value={findedItem}
                isOptionEqualToValue={(option, value) => option.value === value}
                dataset={autocompleteOptions.workArea}
              />
            );
          },
        },

        {
          field: 'progressPercent',
          headerName: 'Készültség %',
          type: 'string',
          editable: true,
          sortable: false,
          valueGetter: (_data, row) => {
            const valueIndex = getValueIndex(values, row.id);
            return values?.serviceTask?.[valueIndex]?.progressPercent;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`serviceTask.${valueIndex}.progressPercent`}
                type="number"
                as={InputUI}
                variant="standard"
                fullWidth
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.target.select()}
                helperText={<FormErrorUI message={errors?.serviceTask?.[valueIndex]?.progressPercent} />}
                onChange={(e) => {
                  setFieldValue(`serviceTask.${valueIndex}.progressPercent`, e.target?.value);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target?.value,
                  });
                }}
                onBlur={async () => await validateField(`serviceTask.${valueIndex}.progressPercent`)}
              />
            );
          },
        },

        {
          field: 'serviceStatusId',
          headerName: 'Státusz',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions.serviceStatus.find(
              (u) => u.value === values.serviceTask?.[valueIndex]?.serviceStatusId
            );
            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions.serviceStatus.find(
              (u) => u.value === values.serviceTask?.[valueIndex]?.serviceStatusId
            );
            return (
              <Field
                name={`serviceTask.${valueIndex}.serviceStatusId`}
                type="text"
                variant="standard"
                as={AutoCompleteSelectUI}
                fullWidth
                helperText={<FormErrorUI message={errors?.serviceTask?.[valueIndex]?.serviceStatusId} />}
                onChange={(_e, newVal) => {
                  const val = newVal?.value ?? null;

                  setFieldValue(`serviceTask.${valueIndex}.serviceStatusId`, val);

                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: val,
                  });
                }}
                onBlur={async () => await validateField(`serviceTask.${valueIndex}.serviceStatusId`)}
                value={findedItem}
                isOptionEqualToValue={(option, value) => option.value === value}
                dataset={autocompleteOptions.serviceStatus}
              />
            );
          },
        },

        {
          field: 'resourceOneId',
          headerName: 'Felelős',
          type: 'string',
          editable: true,
          sortable: false,
          renderCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions.resourceOne.find(
              (u) => u.value === values.serviceTask?.[valueIndex]?.resourceOneId
            );
            return findedItem?.label;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            const findedItem = autocompleteOptions.resourceOne.find(
              (u) => u.value === values.serviceTask?.[valueIndex]?.resourceOneId
            );
            return (
              <Field
                name={`serviceTask.${valueIndex}.resourceOneId`}
                type="text"
                variant="standard"
                as={AutoCompleteSelectUI}
                fullWidth
                helperText={<FormErrorUI message={errors?.serviceTask?.[valueIndex]?.resourceOneId} />}
                onChange={(_e, newVal) => {
                  const val = newVal?.value ?? null;

                  setFieldValue(`serviceTask.${valueIndex}.resourceOneId`, val);

                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: val,
                  });
                }}
                onBlur={async () => await validateField(`serviceTask.${valueIndex}.resourceOneId`)}
                value={findedItem}
                isOptionEqualToValue={(option, value) => option.value === value}
                dataset={autocompleteOptions.resourceOne}
              />
            );
          },
        },

        {
          field: 'attachRequired',
          headerName: 'Melléklet szükséges',
          type: 'boolean',
          editable: true,
          sortable: false,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`serviceTask.${valueIndex}.attachRequired`}
                type="checkbox"
                component={CheckboxUI}
                helperText={<FormErrorUI message={errors?.serviceTask?.[valueIndex]?.attachRequired} />}
                checked={values.serviceTask?.[valueIndex]?.attachRequired ?? false}
                variant="standard"
                onChange={(e) => {
                  setFieldValue(`serviceTask.${valueIndex}.attachRequired`, e.target.checked);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.checked,
                  });
                }}
              />
            );
          },
        },

        {
          field: 'descriptionRequired',
          headerName: 'Leírás szükséges',
          type: 'boolean',
          editable: true,
          sortable: false,
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);
            return (
              <Field
                name={`serviceTask.${valueIndex}.descriptionRequired`}
                type="checkbox"
                component={CheckboxUI}
                helperText={<FormErrorUI message={errors?.serviceTask?.[valueIndex]?.descriptionRequired} />}
                checked={values.serviceTask?.[valueIndex]?.descriptionRequired ?? false}
                variant="standard"
                onChange={(e) => {
                  setFieldValue(`serviceTask.${valueIndex}.descriptionRequired`, e.target.checked);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target.checked,
                  });
                }}
              />
            );
          },
        },
        {
          field: 'note',
          headerName: 'Megjegyzés',
          type: 'string',
          editable: true,
          sortable: false,
          valueGetter: (_data, row) => {
            const valueIndex = getValueIndex(values, row.id);
            return values?.serviceTask?.[valueIndex]?.note;
          },
          renderEditCell: (data) => {
            const valueIndex = getValueIndex(values, data.id);

            return (
              <Field
                name={`serviceTask.${valueIndex}.note`}
                type="text"
                as={InputUI}
                variant="standard"
                fullWidth
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.target.select()}
                helperText={<FormErrorUI message={errors?.serviceTask?.[valueIndex]?.note} />}
                onChange={(e) => {
                  setFieldValue(`serviceTask.${valueIndex}.note`, e.target?.value);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target?.value,
                  });
                }}
                onBlur={async () => await validateField(`serviceTask.${valueIndex}.note`)}
              />
            );
          },
        },

        {
          field: 'actions',
          type: 'actions',
          headerName: '',
          cellClassName: 'actions',
          sortable: false,
          getActions: ({ id, row }) => {
            return [
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  const valueIndex = getValueIndex(values, id);

                  const materials = values.serviceMaterial.filter((sm) => sm.serviceTaskId !== id);
                  const checklists = values.serviceTaskCheckList.filter((sc) => sc.serviceTaskId !== id);

                  setFieldValue(`serviceMaterial`, materials);
                  setFieldValue(`serviceTaskCheckList`, checklists);

                  return remove(valueIndex);
                }}
                color="inherit"
                disabled={disabled}
              />,
            ];
          },
        },

        {
          field: 'checklistActions',
          type: 'actions',
          headerName: '',
          cellClassName: 'actions',
          sortable: false,
          getActions: ({ id, row }) => {
            return [
              <GridActionsCellItem
                icon={<Checklist />}
                label="Feladatlista"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  //const valueIndex = getValueIndex(values, id);

                  setChecklistDialogData({
                    open: true,
                    serviceTaskId: id,
                  });
                }}
                color="inherit"
                disabled={disabled}
              />,
            ];
          },
        },
      ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [autocompleteData.quantityUnit, values.serviceTask, errors, setFieldValue, validateField, disabled]
  );

  const handleCloseChecklistDialog = () => {
    setChecklistDialogData(defaultDialogData);
  };

  return (
    <>
      <div className="overflow-x-auto">
        <div className="align-middle">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <FieldArray name="serviceTask">
              {({ remove, push }) => (
                <DatagridPremiumUi
                  apiRef={apiRef}
                  datagridIdentifier={DataGridIdentifiers.ServiceTask}
                  height={480}
                  columns={columns(remove)}
                  rows={values?.serviceTask ?? []}
                  editMode="row"
                  slots={{
                    toolbar: EditToolbar,
                  }}
                  slotProps={{
                    toolbar: {
                      disabled,
                      push,
                      defaultServiceTask,
                    },
                  }}
                  onCellDoubleClick={(_params, event) => {
                    event.defaultMuiPrevented = true;
                  }}
                  headerFilters={false}
                  getRowHeight={() => 'auto'}
                  onCellClick={(params, event) => {
                    if (disabled) return;
                    focusedFieldRef.current = params.field;
                    const rowsInEditMode = Object.keys(apiRef.current.state.editRows);

                    for (const rowId of rowsInEditMode) {
                      if (params.row.id.toString() !== rowId) {
                        apiRef.current.stopRowEditMode({ id: rowId });
                      }
                    }

                    const rowMode = apiRef.current.getRowMode(params.row.id);
                    if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                  }}
                  onRowEditStop={() => {
                    focusedFieldRef.current = null;
                  }}
                />
              )}
            </FieldArray>
          </div>
        </div>
      </div>
      <ServiceTaskChecklistDialog
        open={checklistDialogData.open}
        onClose={handleCloseChecklistDialog}
        maxWidth="md"
        selectedTaskId={checklistDialogData.serviceTaskId}
        selectedValues={selectedValues}
      />
    </>
  );
};

export default ServiceTask;
